import { createEffect, createEvent, createStore, sample } from 'effector';
import { ActivityFilterType, getActivities, readActivity, readAllActivity } from '@nx-frontend/shared/api';
import {
  ActivityStreamResponse,
  NotificationActivityEnrichedRecord,
  NotificationPanelContext,
  NotificationPanelState,
  NotificationPanelWorkspaceFilter,
  NotificationPanelWorkspaceFilterValues,
  NotificationPanelWorkspaceState,
  NotificationsPortalContext,
} from '@nx-frontend/shared/types';

export type NotificationTabs = 'important' | 'updates';

export const initialActivitiesState: NotificationPanelState = {
  /**
   * current workspaceId
   */
  workspaceId: '',
  workspaces: {},
  show: false,
  isFetching: false,
  isLoaded: false,
  context: {
    gatewayUrl: '',
    sessionId: '',
    userId: 0,
    fetchChatActivities: true,
    portalContext: {
      portalId: '',
      clientIds: [],
      role: 'owner',
      settings: {
        name: '',
        domain: '',
      },
      mode: 'webnotes',
      isPortalAccessDenied: false,
    },
  },
};

const initialFilterState: NotificationPanelWorkspaceFilter = {
  show: false,
  showFilterByPlace: false,
  showFilterByUser: false,
  workspaceId: null,
  portalId: null,
  userIds: [],
};

const initialWorkspaceState: NotificationPanelWorkspaceState = {
  entities: {},
  tabs: {},
  hasImportant: false,
  tab: 'important',
  filter: initialFilterState,
};

interface FetchMoreActivitiesEventPayload {
  hostUrl?: string;
  workspaceId: string;
  sessionId: string;
  tab?: NotificationTabs;
  userId?: number;
  beforeId?: number;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
}

interface ReadActivityEventPayload {
  activityId: string;
  globalId: string;
  hostUrl: string;
  workspaceId: string;
  sessionId: string;
  read: boolean;
  userId: number;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
}

interface MarkAllActivityAsReadEventPayload {
  hostUrl: string;
  workspaceId: string;
  sessionId: string;
  userId: number;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
}

interface SetShowUnreadEventPayload {
  hostUrl?: string;
  workspaceId: string;
  sessionId: string;
  showUnread: boolean;
  userId: number;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
}

const NOTIFICATION_PANEL_FILTER_LS_KEY = 'notification-panel-filter';

export const $activitiesStore = createStore<NotificationPanelState>(initialActivitiesState);

export const setNotificationPanelContextEvent = createEvent<NotificationPanelContext>();
export const fetchMoreActivitiesEvent = createEvent<FetchMoreActivitiesEventPayload>();
export const readActivityEvent = createEvent<ReadActivityEventPayload>();
export const markAllActivityAsReadEvent = createEvent<MarkAllActivityAsReadEventPayload>();
export const setShowUnreadEvent = createEvent<SetShowUnreadEventPayload>();
export const setNotificationPanelVisibilityEvent = createEvent<boolean>();
export const setNotificationPanelTabEvent = createEvent<NotificationTabs>();
export const setShowNotificationPanelFilterEvent = createEvent<boolean>();
export const setShowNotificationPanelFilterByPlaceEvent = createEvent<boolean>();
export const setShowNotificationPanelFilterByUserEvent = createEvent<boolean>();
export const setNotificationPanelFilterEvent = createEvent<Partial<NotificationPanelWorkspaceFilter>>();
export const setNotificationPanelFilterValuesEvent = createEvent<Partial<NotificationPanelWorkspaceFilterValues>>();

// called externally, might be called with the same worksapceId as current
export const setNotificationPanelCurrentWorkspaceIdEvent = createEvent<string>();
// called internally when workspaceId actually changes
export const changeNotificationPanelCurrentWorkspaceIdEvent = createEvent<string>();

interface FetchActivitiesFxParams {
  hostUrl?: string;
  sessionId: string;
  workspaceId: string;
  beforeId?: number;
  tab?: NotificationTabs;
  userId?: number;
  unread?: boolean;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
  filterState?: NotificationPanelWorkspaceFilterValues;
}

interface FetchActivitiesFxResponse {
  activities: ActivityStreamResponse;
  workspaceId: string;
  tab: NotificationTabs;
  portalContext?: NotificationsPortalContext;
}

const LOAD_ACTIVITY_LIMIT = 20;

interface GetActivityFilterParams {
  tab: NotificationTabs;
  userId?: number;
  fetchChatActivities: boolean;
  portalContext?: NotificationsPortalContext;
  filterState?: NotificationPanelWorkspaceFilterValues;
}

/**
 * Get filter for fetch activities
 * @param params
 * @returns
 */
function getActivityFilter(params: GetActivityFilterParams): ActivityFilterType {
  const { tab, portalContext, userId, fetchChatActivities } = params;
  let filter: ActivityFilterType = {};

  const portalClientIds: number[] = []; // portal client should not see notifications from other clients
  if (portalContext?.role === 'client') {
    portalClientIds.push(...portalContext.clientIds);
  }

  let userFilter: ActivityFilterType = {
    ...(userId ? { userId: { $notIn: [0, ...portalClientIds] } } : {}),
  };

  const { portalId, isPortalAccessDenied, mode } = portalContext || { isPortalAccessDenied: false };
  let portalFilter: ActivityFilterType = {
    ...(portalId && mode === 'portals' ? { portalId } : {}),
    ...(portalId && mode === 'webnotes' && isPortalAccessDenied ? { portalId: null } : {}),
  };

  let importantFilter: ActivityFilterType = {};
  const { filterState } = params;
  if (filterState && mode === 'webnotes') {
    if (filterState.portalId && !isPortalAccessDenied && !filterState.workspaceId) {
      portalFilter = { portalId: filterState.portalId };
      importantFilter = portalFilter;
    }
    if (filterState.workspaceId && !filterState.portalId) {
      portalFilter = { portalId: null };
      importantFilter = portalFilter;
    }
    if (filterState.userIds.length) {
      userFilter = {
        userId: filterState.userIds,
      };
    }
  }

  if (tab === 'important') {
    filter = {
      $or: [
        {
          action: ['mention', 'reply', 'message'],
          ...(!fetchChatActivities ? { objectType: { $notIn: ['chat'] } } : {}),
          ...userFilter,
          ...portalFilter,
        },
        ...(fetchChatActivities
          ? [
              {
                objectType: ['chat'],
                ...userFilter,
                ...importantFilter,
              },
            ]
          : []),
      ],
    };
  }
  if (tab === 'updates') {
    filter = {
      $or: [
        {
          action: {
            $notIn: ['mention', 'reply', 'message'],
          },
        },
        {
          objectType: ['note', 'workspace', 'folder'],
          action: ['mention'],
        },
      ],
      ...userFilter,
      ...portalFilter,
    };
  }

  return filter;
}

const filterIssueActivities =
  (portalContext?: NotificationsPortalContext) =>
  (activity: NotificationActivityEnrichedRecord): boolean => {
    // TODO: fast fix, have to find reason why extra is not set!
    const isCommentExtraEmpty = activity.objectType === 'comment' && !activity.extra;

    // Fix old comment witout taskId
    const isCommentWithoutNoteAndTask =
      activity.objectType === 'comment' &&
      ['mention', 'reply'].includes(activity.action) &&
      !activity.note &&
      !activity.task;

    // Fix for old records without portalId
    let skipPortalActivity = false;
    const { mode, portalId, isPortalAccessDenied } = portalContext || {};
    if (portalId && mode === 'webnotes' && isPortalAccessDenied && activity.note?.isSharedForPortal) {
      skipPortalActivity = true;
    }

    return !isCommentExtraEmpty && !skipPortalActivity && !isCommentWithoutNoteAndTask;
  };

export const fetchActivitiesFx = createEffect(
  async (params: FetchActivitiesFxParams): Promise<FetchActivitiesFxResponse> => {
    const {
      hostUrl,
      sessionId,
      workspaceId,
      beforeId,
      tab = 'important',
      userId,
      unread = undefined,
      fetchChatActivities = true,
      portalContext,
      filterState,
    } = params;

    const filter: ActivityFilterType = getActivityFilter({
      tab,
      userId,
      fetchChatActivities,
      portalContext,
      filterState,
    });

    const activities = await getActivities({
      hostUrl,
      workspaceId,
      sessionId: sessionId,
      beforeId,
      filter,
      limit: LOAD_ACTIVITY_LIMIT,
      ...(tab === 'important' ? { unread } : {}),
    });

    return { activities, workspaceId, tab, portalContext };
  }
);

const stateFetchActivityEvent = createEvent<FetchMoreActivitiesEventPayload>();

const getFetchActivitiesFxParams = (
  state: NotificationPanelState,
  params: FetchMoreActivitiesEventPayload
): FetchActivitiesFxParams => {
  const workspace = state.workspaces[state.workspaceId] || initialWorkspaceState;
  const filter = workspace.filter || initialFilterState;
  const payload: FetchActivitiesFxParams = {
    hostUrl: state.context.gatewayUrl,
    workspaceId: state.workspaceId,
    sessionId: state.context.sessionId,
    beforeId: params.beforeId,
    tab: params.tab || workspace.tab,
    userId: state.context.userId,
    unread: workspace.showUnread,
    fetchChatActivities: state.context.fetchChatActivities,
    portalContext: state.context.portalContext,
    filterState: {
      workspaceId: filter.workspaceId,
      portalId: filter.portalId,
      userIds: filter.userIds,
    },
  };
  return payload;
};

sample({
  source: $activitiesStore,
  clock: fetchMoreActivitiesEvent,
  fn: getFetchActivitiesFxParams,
  filter: (state, params) => {
    const workspaceState = state.workspaces[params.workspaceId] || { entities: {}, tabs: {} };
    const tabState = workspaceState.tabs[params.tab || 'important'] ?? { isFetching: false, isLoaded: false };
    const next = tabState.isFetching || tabState.isLoaded ? false : true;
    return next;
  },
  target: [stateFetchActivityEvent, fetchActivitiesFx],
});

sample({
  source: $activitiesStore,
  clock: setNotificationPanelCurrentWorkspaceIdEvent,
  filter: (state, workspaceId) => state.workspaceId !== workspaceId,
  fn: (_, workspaceId) => {
    return workspaceId;
  },
  target: changeNotificationPanelCurrentWorkspaceIdEvent,
});

$activitiesStore.on(changeNotificationPanelCurrentWorkspaceIdEvent, (state, workspaceId) => {
  return {
    ...state,
    workspaceId,
  };
});

$activitiesStore.on(fetchActivitiesFx, (state) => ({
  ...state,
  isFetching: true,
  isLoaded: false,
}));

$activitiesStore.on(fetchActivitiesFx.done, (state) => ({
  ...state,
  isFetching: false,
}));

$activitiesStore.on(fetchActivitiesFx.fail, (state) => ({
  ...state,
  isFetching: false,
}));

$activitiesStore.on(stateFetchActivityEvent, (state, { workspaceId, tab = 'important' }) => {
  const workspace = state.workspaces[workspaceId] || { entities: {}, tabs: {}, hasImportant: false };
  const tabs = workspace.tabs;
  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        tabs: {
          ...tabs,
          [tab]: {
            isFetching: true,
            isLoaded: false,
          },
        },
      },
    },
  };
});

function enrichActivities(activities: ActivityStreamResponse): NotificationActivityEnrichedRecord[] {
  return activities.activityStream.map((activity) => {
    const user = activities.users.find((user) => user.id === activity.userId);
    const note = activities.notes.find((note) => note.globalId === activity.nodeId);
    const avatar = activities.avatars.find((avatar) => avatar.userId === activity.userId);
    const noteParent = activities.noteParents?.find(({ globalId }) => globalId === note?.parentId);
    const comment = activities.comments.find(
      (comment) => comment.globalId === activity.objectId && activity.objectType === 'comment'
    );
    const mention = activities.mentions.find((mention) => mention.globalId === activity.nodeId);
    const isParentDeleted = activities.noteDeletedParent?.some(
      (globalId) => globalId === note?.globalId || noteParent?.globalId === globalId
    );
    const task = activities.tasks?.find((task) => task.globalId === activity.extra?.taskId);
    const portalPage = activities.portalPages?.find((page) => page.noteIds.includes(note?.globalId ?? ''));
    const file = activities.files?.find((file) => file.globalId === activity.objectId);

    return {
      ...activity,
      user,
      avatar,
      note,
      noteParent,
      comment,
      mention,
      ...(activity.objectType === 'chat' ? { users: [...activities.users] } : {}),
      isParentDeleted,
      task,
      portalPage,
      file,
    };
  });
}

$activitiesStore.on(fetchActivitiesFx.doneData, (state, { activities, workspaceId, tab, portalContext }) => {
  const updatedWorkspaces: Record<string, NotificationPanelWorkspaceState> = {};
  const currentWorkspaceState = state.workspaces[workspaceId] ?? initialWorkspaceState;
  const filter = currentWorkspaceState.filter || initialFilterState;

  const mergedEntities = { ...currentWorkspaceState.entities };
  const hasImportant = currentWorkspaceState.hasImportant;

  const currentMinUserActivityStreamItemIdForTab =
    currentWorkspaceState.tabs[tab]?.minUserActivityStreamItemId ?? Infinity;
  let minFetchedUserActivityStreamItemId = currentMinUserActivityStreamItemIdForTab;

  const mappedActivities = enrichActivities(activities);

  const activityStream = mappedActivities.filter(filterIssueActivities(portalContext));

  const originalActivityStream = activities.activityStream;

  originalActivityStream.forEach((activity) => {
    if (activity.userActivityStreamItemId < minFetchedUserActivityStreamItemId) {
      minFetchedUserActivityStreamItemId = activity.userActivityStreamItemId;
    }
  });

  for (const activity of activityStream) {
    mergedEntities[activity.globalId] = activity;
    if (state.context.portalContext?.mode === 'webnotes') {
      // hide old notification without portalId, with portal share note
      if (filter.workspaceId && !activity.portalId && activity.note?.isSharedForPortal) {
        delete mergedEntities[activity.globalId];
      }
    }
  }

  const isLoaded = originalActivityStream.length === 0 || originalActivityStream.length < LOAD_ACTIVITY_LIMIT;
  const tabs = currentWorkspaceState.tabs ?? {};
  const isFirstLoad = currentWorkspaceState.tabs[tab]?.isFirstLoad;
  let isFirstLoadEmpty = false;
  if (isFirstLoad === undefined) {
    isFirstLoadEmpty = originalActivityStream.length === 0;
  }

  let minUserActivityStreamItemId = currentWorkspaceState.tabs[tab]?.minUserActivityStreamItemId;
  if (originalActivityStream.length > 0) {
    minUserActivityStreamItemId = Math.min(
      currentMinUserActivityStreamItemIdForTab,
      minFetchedUserActivityStreamItemId
    );
  }

  const updatedWorkspaceState: NotificationPanelWorkspaceState = {
    ...currentWorkspaceState,
    entities: mergedEntities,
    hasImportant: hasImportant,
    tabs: {
      ...tabs,
      [tab]: {
        isFetching: false,
        isLoaded,
        isFirstLoad: false,
        isFirstLoadEmpty,
        minUserActivityStreamItemId,
      },
    },
  };

  updatedWorkspaces[workspaceId] = updatedWorkspaceState;

  const isResponseEmpty = activities.activityStream.length === 0;
  const isLessThanLimit = activities.activityStream.length < LOAD_ACTIVITY_LIMIT;

  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      ...updatedWorkspaces,
    },
    isLoaded: isResponseEmpty || isLessThanLimit,
  };
});

interface FetchImportantActivitiesFxParams {
  sessionId: string;
  workspaceId: string;
  hostUrl: string;
  userId: number;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
}

interface FetchImportantActivitiesFxResponse {
  workspaceId: string;
  hasImportant: boolean;
}

const fetchImportantActivitiesFn = async (
  params: FetchImportantActivitiesFxParams
): Promise<FetchImportantActivitiesFxResponse> => {
  const { hostUrl, workspaceId, sessionId, userId, fetchChatActivities = true, portalContext } = params;

  const res = await getActivities({
    hostUrl,
    workspaceId,
    limit: 1,
    sessionId,
    filter: getActivityFilter({ tab: 'important', portalContext, userId, fetchChatActivities }),
    unread: true,
  });

  return {
    workspaceId,
    hasImportant: enrichActivities(res)?.filter(filterIssueActivities(portalContext)).length ? true : false,
  };
};

export const fetchImportantActivitiesFx = createEffect(fetchImportantActivitiesFn);

let TID: NodeJS.Timeout | number | null = null;
const fetchImportantActivitiesFnDebounced = createEffect(
  async (params: FetchImportantActivitiesFxParams): Promise<FetchImportantActivitiesFxResponse> => {
    TID && clearTimeout(TID);
    return new Promise((resolve) => {
      TID = setTimeout(() => {
        resolve(fetchImportantActivitiesFn(params));
      }, 500);
    });
  }
);

const onfetchImportantActivitiesEventDone = (
  state: NotificationPanelState,
  payload: FetchImportantActivitiesFxResponse
) => {
  const { hasImportant, workspaceId } = payload;
  const workspace = state.workspaces[workspaceId] || { entities: {}, tabs: {}, hasImportant: false, tab: 'important' };
  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        hasImportant,
      },
    },
  };
};

$activitiesStore.on(fetchImportantActivitiesFx.doneData, onfetchImportantActivitiesEventDone);
$activitiesStore.on(fetchImportantActivitiesFnDebounced.doneData, onfetchImportantActivitiesEventDone);

interface ReadActivityEffectResponse {
  workspaceId: string;
  activityId: string;
  read: boolean;
  params: FetchImportantActivitiesFxParams;
}

const readActivityFxFn = async (params: ReadActivityEventPayload): Promise<ReadActivityEffectResponse> => {
  const { activityId, hostUrl, workspaceId, sessionId, read, userId, fetchChatActivities, portalContext } = params;
  await readActivity({
    hostUrl,
    workspaceId,
    sessionId,
    activityId,
    read,
  });
  return {
    workspaceId,
    activityId,
    read,
    params: {
      hostUrl,
      sessionId,
      workspaceId,
      userId,
      fetchChatActivities,
      portalContext,
    },
  };
};

const READ_ACTIVITY_TID: Record<string, NodeJS.Timeout | number | null> = {};
export const readActivityFx = createEffect(
  async (params: ReadActivityEventPayload): Promise<ReadActivityEffectResponse> => {
    const tid = READ_ACTIVITY_TID[params.activityId];
    tid && clearTimeout(tid);
    return new Promise((resolve) => {
      READ_ACTIVITY_TID[params.activityId] = setTimeout(() => {
        resolve(readActivityFxFn(params));
        delete READ_ACTIVITY_TID[params.activityId];
      }, 500);
    });
  }
);

$activitiesStore.on(readActivityEvent, (state, { workspaceId, globalId, read }) => {
  const workspace = state.workspaces[workspaceId] || { entities: {}, tabs: {}, hasImportant: false, tab: 'important' };
  const entities = { ...workspace.entities };

  const activity = entities[globalId];
  if (!activity) {
    return state;
  }

  const newState = {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        entities: {
          ...entities,
          [globalId]: {
            ...activity,
            userActivityStreamItemRead: read,
          },
        },
      },
    },
  };

  return newState;
});

sample({
  clock: readActivityEvent,
  fn: (payload) => payload,
  target: readActivityFx,
});

interface ReadAllActivityEffectResponse {
  workspaceId: string;
  params: FetchImportantActivitiesFxParams;
}

const markAllActivityAsReadFx = createEffect(
  async (params: MarkAllActivityAsReadEventPayload): Promise<ReadAllActivityEffectResponse> => {
    const { hostUrl, workspaceId, sessionId, userId } = params;
    await readAllActivity({
      hostUrl,
      workspaceId,
      sessionId,
    });
    return {
      workspaceId,
      params: {
        hostUrl,
        sessionId,
        workspaceId,
        userId,
      },
    };
  }
);

$activitiesStore.on(markAllActivityAsReadEvent, (state, { workspaceId }) => {
  const workspace = state.workspaces[workspaceId] || { entities: {}, tabs: {}, hasImportant: false, tab: 'important' };
  const entities = { ...workspace.entities };

  const unreadItems = Object.values(entities).filter((item) => !item.userActivityStreamItemRead);

  unreadItems.forEach((activity) => {
    entities[activity.globalId] = {
      ...activity,
      userActivityStreamItemRead: true,
    };
  });

  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        entities,
      },
    },
  };
});

sample({
  clock: markAllActivityAsReadEvent,
  fn: (payload) => payload,
  target: markAllActivityAsReadFx,
});

sample({
  clock: [readActivityFx.doneData, markAllActivityAsReadFx.doneData],
  fn: (payload) => {
    return payload.params;
  },
  target: fetchImportantActivitiesFnDebounced,
});

const onSetShowUnreadEvent = createEvent<SetShowUnreadEventPayload>();

$activitiesStore.on(onSetShowUnreadEvent, (state, { workspaceId, showUnread }) => {
  const workspace = state.workspaces[workspaceId] || { entities: {}, tabs: {}, hasImportant: false, tab: 'important' };
  const important = workspace.tabs.important ? workspace.tabs.important : {};
  const updates = workspace.tabs.updates ? workspace.tabs.updates : {};

  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        entities: {},
        tabs: {
          ...workspace.tabs,
          important: {
            ...important,
            isFetching: true,
            isLoaded: false,
            minUserActivityStreamItemId: undefined,
          },
          updates: {
            ...updates,
            isFetching: false,
            isLoaded: false,
            minUserActivityStreamItemId: undefined,
          },
        },
        showUnread,
      },
    },
  };
});

sample({
  source: $activitiesStore,
  clock: setShowUnreadEvent,
  fn: (state, payload) => {
    const params = getFetchActivitiesFxParams(state, payload);
    return { ...payload, ...params, unread: payload.showUnread, tab: 'important' };
  },
  target: [onSetShowUnreadEvent, fetchActivitiesFx],
});

$activitiesStore.on(setNotificationPanelVisibilityEvent, (state, show) => {
  return {
    ...state,
    show,
  };
});

export const $isNotificationPanelShow = $activitiesStore.map((store) => {
  return store.show;
});

$activitiesStore.on(setNotificationPanelTabEvent, (state, tab) => {
  const workspace = state.workspaces[state.workspaceId] || { entities: {}, tabs: {}, hasImportant: false, tab };
  if (!state.workspaceId) {
    return state;
  }

  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [state.workspaceId]: {
        ...workspace,
        tab,
      },
    },
  };
});

export const $hasImportantActivities = $activitiesStore.map((store) => {
  const workspace = store.workspaces[store.workspaceId] || { hasImportant: false };
  return workspace.hasImportant;
});

$activitiesStore.on(setShowNotificationPanelFilterEvent, (state, show) => {
  const workspaceId = state.workspaceId;

  const workspace = state.workspaces[workspaceId] || {
    entities: {},
    tabs: {},
    hasImportant: false,
    tab: 'important',
    filter: initialFilterState,
  };
  const filter: NotificationPanelWorkspaceFilter = workspace.filter || initialFilterState;
  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        filter: {
          ...filter,
          show,
          showFilterByPlace: false,
          showFilterByUser: false,
        },
      },
    },
  };
});

export const $notificationPanelFilter = $activitiesStore.map((store) => {
  const workspace = store.workspaces[store.workspaceId];
  return workspace?.filter || initialFilterState;
});

$activitiesStore.on(setShowNotificationPanelFilterByPlaceEvent, (state, show) => {
  const workspaceId = state.workspaceId;
  const workspace = state.workspaces[workspaceId] || { entities: {}, tabs: {}, hasImportant: false, tab: 'important' };
  const filter = workspace.filter || initialFilterState;
  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        filter: {
          ...filter,
          showFilterByPlace: show,
          showFilterByUser: false,
        },
      },
    },
  };
});

$activitiesStore.on(setShowNotificationPanelFilterByUserEvent, (state, show) => {
  const workspaceId = state.workspaceId;
  const workspace = state.workspaces[workspaceId] || initialWorkspaceState;
  const filter = workspace.filter || initialFilterState;
  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        filter: {
          ...filter,
          showFilterByUser: show,
          showFilterByPlace: false,
        },
      },
    },
  };
});

let DEBOUNCE_TIMER: NodeJS.Timeout | number | null = null;
const onNotificationPanelFilterChangeFx = createEffect(async () => {
  DEBOUNCE_TIMER && clearTimeout(DEBOUNCE_TIMER);
  return new Promise((resolve) => {
    DEBOUNCE_TIMER = setTimeout(() => {
      resolve(true);
    }, 1000);
  });
});

const setNotificationPanelFilterToLocalStorageFx = createEffect(
  (params: NotificationPanelWorkspaceFilterValues & { currentWorkspaceId: string }) => {
    const lsValue = localStorage.getItem(NOTIFICATION_PANEL_FILTER_LS_KEY) || '{}';
    const lsFilter = JSON.parse(lsValue);
    const { currentWorkspaceId, ...filter } = params;
    const newFilter = {
      ...lsFilter,
      [params.currentWorkspaceId]: {
        ...lsFilter[params.currentWorkspaceId],
        ...filter,
      },
    };
    localStorage.setItem(NOTIFICATION_PANEL_FILTER_LS_KEY, JSON.stringify(newFilter));
  }
);

export const loadNotificationPanelFilterFromLocalStorageFx = createEffect((workspaceId: string) => {
  const lsValue = localStorage.getItem(NOTIFICATION_PANEL_FILTER_LS_KEY) || '{}';
  const lsFilter = JSON.parse(lsValue);
  return { filter: lsFilter[workspaceId] || {}, workspaceId };
});

$activitiesStore.on(loadNotificationPanelFilterFromLocalStorageFx.doneData, (state, payload) => {
  const workspace = state.workspaces[payload.workspaceId] || initialWorkspaceState;
  const filter = workspace.filter || initialFilterState;

  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [payload.workspaceId]: {
        ...workspace,
        filter: {
          ...filter,
          ...payload.filter,
        },
      },
    },
  };
});

/**
 * Check are filters values was changed, than trigger debounce event for fetch activities
 */
sample({
  source: $activitiesStore,
  clock: setNotificationPanelFilterValuesEvent,
  fn: (state, payload) => {
    return { ...payload, currentWorkspaceId: state.workspaceId };
  },
  filter: (state, payload) => {
    const workspace = state.workspaces[state.workspaceId] || initialWorkspaceState;
    const filter = workspace.filter || initialFilterState;
    const prevValues = JSON.stringify({
      workspaceId: filter.workspaceId,
      portalId: filter.portalId,
      userIds: filter.userIds,
    });
    const newValues = JSON.stringify({
      workspaceId: payload.workspaceId === undefined ? filter.workspaceId : payload.workspaceId,
      portalId: payload.portalId === undefined ? filter.portalId : payload.portalId,
      userIds: payload.userIds === undefined ? filter.userIds : payload.userIds,
    });
    return prevValues !== newValues;
  },
  target: [
    setNotificationPanelFilterEvent,
    onNotificationPanelFilterChangeFx,
    setNotificationPanelFilterToLocalStorageFx,
  ],
});

$activitiesStore.on(onNotificationPanelFilterChangeFx.doneData, (state) => {
  const workspace = state.workspaces[state.workspaceId] || initialWorkspaceState;
  const important = workspace.tabs.important ? workspace.tabs.important : {};
  const updates = workspace.tabs.updates ? workspace.tabs.updates : {};

  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [state.workspaceId]: {
        ...workspace,
        entities: {},
        tabs: {
          ...workspace.tabs,
          important: {
            ...important,
            isFetching: false,
            isLoaded: false,
            minUserActivityStreamItemId: undefined,
          },
          updates: {
            ...updates,
            isFetching: false,
            isLoaded: false,
            minUserActivityStreamItemId: undefined,
          },
        },
      },
    },
  };
});

sample({
  source: $activitiesStore,
  clock: onNotificationPanelFilterChangeFx.done,
  fn: (store) => ({ workspaceId: store.workspaceId, sessionId: store.context.sessionId }),
  target: fetchMoreActivitiesEvent,
});

$activitiesStore.on(setNotificationPanelFilterEvent, (state, filter) => {
  const workspaceId = state.workspaceId;
  const workspace = state.workspaces[workspaceId] || initialWorkspaceState;
  const currentFilter = workspace.filter || initialFilterState;
  return {
    ...state,
    workspaces: {
      ...state.workspaces,
      [workspaceId]: {
        ...workspace,
        filter: {
          ...currentFilter,
          ...filter,
        },
      },
    },
  };
});

export const $notificationPanelWorkspaceId = $activitiesStore.map((store) => store.workspaceId);

export const $isNotificationPanelFilterActive = $notificationPanelFilter.map((filter) => {
  return filter.workspaceId || filter.portalId || filter.userIds.length;
});

$activitiesStore.on(setNotificationPanelContextEvent, (state, context) => {
  return {
    ...state,
    context,
  };
});
